import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataState,
  Input,
  Modal,
  PageHeader,
  Select,
  Table,
  useToast,
  type Column,
} from "@vms/ui-web";
import { useApi } from "../auth/AuthProvider";
import { useQuery } from "../api/queries";

interface LicenceRow {
  venueId: string;
  venueName: string;
  venueSlug: string;
  organisationId: string;
  organisationName: string;
  licencePlan: string;
  licenceStatus: "active" | "suspended" | "revoked";
  issuedAt: string | null;
  expiresAt: string | null;
  banner: "ok" | "expiring-soon" | "expired" | "revoked" | "term-expired";
  hubId: string | null;
  termStartsAt: string | null;
  termEndsAt: string | null;
  planAtTerm: string | null;
  currentTermId: string | null;
}

interface TermRow {
  id: string;
  venueId: string;
  startsAt: string;
  endsAt: string;
  durationMonths: number;
  planAtTerm: string;
  origin: "initial" | "renewal" | "manual-edit";
  previousTermId: string | null;
  notes: string | null;
  createdAt: string;
  isCurrent: boolean;
  coversNow: boolean;
}

const BANNER_TONE: Record<LicenceRow["banner"], string> = {
  ok: "bg-green-100 text-green-700",
  "expiring-soon": "bg-amber-100 text-amber-700",
  expired: "bg-red-100 text-red-700",
  revoked: "bg-gray-200 text-gray-800",
  "term-expired": "bg-red-100 text-red-700",
};

const BANNER_LABEL: Record<LicenceRow["banner"], string> = {
  ok: "ok",
  "expiring-soon": "expiring soon",
  expired: "expired",
  revoked: "revoked",
  "term-expired": "term expired",
};

export function Licences() {
  const licences = useQuery<LicenceRow[]>("/api/admin/licences");
  const api = useApi();
  const { addToast } = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<LicenceRow | null>(null);
  const [renewing, setRenewing] = useState<LicenceRow | null>(null);
  const [historyFor, setHistoryFor] = useState<LicenceRow | null>(null);
  const [revealKey, setRevealKey] = useState<{ venueName: string; venueKey: string } | null>(null);

  async function issueVenueKey(row: LicenceRow) {
    setBusyId(row.venueId);
    try {
      const res = await api(`/api/admin/venues/${row.venueId}/issue-venue-key`, { method: "POST" });
      const json = await res.json();
      if (!res.ok || !json.success) {
        addToast(json.error ?? "Failed to issue venueKey", "error");
        return;
      }
      addToast(`${row.venueName}: venueKey issued`);
      setRevealKey({ venueName: row.venueName, venueKey: json.data.venueKey });
      licences.refetch();
    } finally {
      setBusyId(null);
    }
  }

  async function refresh(row: LicenceRow) {
    setBusyId(row.venueId);
    try {
      const res = await api(`/api/admin/venues/${row.venueId}/refresh-cert`, { method: "POST" });
      const json = await res.json();
      if (!res.ok || !json.success) {
        addToast(json.error ?? "Failed to refresh cert", "error");
        return;
      }
      addToast(`${row.venueName}: cert refreshed`);
      licences.refetch();
    } finally {
      setBusyId(null);
    }
  }

  async function revoke(row: LicenceRow) {
    setBusyId(row.venueId);
    try {
      const res = await api(`/api/admin/venues/${row.venueId}/revoke-licence`, { method: "POST" });
      const json = await res.json();
      if (!res.ok || !json.success) {
        addToast(json.error ?? "Failed to revoke", "error");
        return;
      }
      addToast(`${row.venueName}: licence revoked`);
      licences.refetch();
    } finally {
      setBusyId(null);
      setRevoking(null);
    }
  }

  async function restore(row: LicenceRow) {
    setBusyId(row.venueId);
    try {
      const res = await api(`/api/admin/venues/${row.venueId}/restore-licence`, { method: "POST" });
      const json = await res.json();
      if (!res.ok || !json.success) {
        addToast(json.error ?? "Failed to restore", "error");
        return;
      }
      addToast(`${row.venueName}: licence active`);
      licences.refetch();
    } finally {
      setBusyId(null);
    }
  }

  const columns: Column<LicenceRow>[] = [
    {
      key: "venue",
      header: "Venue",
      render: (r) => (
        <div>
          <div className="font-medium text-gray-900">{r.venueName}</div>
          <div className="text-xs text-gray-500">{r.organisationName}</div>
        </div>
      ),
    },
    {
      key: "plan",
      header: "Plan",
      render: (r) => <Badge className="bg-gray-100 text-gray-800">{r.licencePlan}</Badge>,
    },
    {
      key: "termEndsAt",
      header: "Term ends",
      render: (r) => (
        <span className="text-sm text-gray-700">
          {r.termEndsAt ? (
            <>
              {new Date(r.termEndsAt).toLocaleDateString()}
              <span className="ml-1 text-xs text-gray-500">{relative(r.termEndsAt)}</span>
            </>
          ) : (
            "—"
          )}
        </span>
      ),
    },
    {
      key: "banner",
      header: "Banner",
      render: (r) => <Badge className={BANNER_TONE[r.banner]}>{BANNER_LABEL[r.banner]}</Badge>,
    },
    {
      key: "expiresAt",
      header: "Cert expires",
      render: (r) => (
        <span className="text-sm text-gray-700">
          {r.expiresAt ? new Date(r.expiresAt).toLocaleString() : "—"}
        </span>
      ),
    },
    {
      key: "issuedAt",
      header: "Last issued",
      render: (r) => (
        <span className="text-xs text-gray-500">
          {r.issuedAt ? new Date(r.issuedAt).toLocaleString() : "Never"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (r) => (
        <div className="text-right space-x-2">
          <Button size="sm" variant="secondary" onClick={() => setHistoryFor(r)}>
            Terms
          </Button>
          <Button size="sm" onClick={() => setRenewing(r)}>
            Renew
          </Button>
          {r.hubId ? (
            <Button
              size="sm"
              variant="secondary"
              loading={busyId === r.venueId}
              onClick={() => refresh(r)}
            >
              Refresh cert
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              loading={busyId === r.venueId}
              onClick={() => issueVenueKey(r)}
            >
              Issue venueKey
            </Button>
          )}
          {r.licenceStatus === "revoked" ? (
            <Button
              size="sm"
              loading={busyId === r.venueId}
              onClick={() => restore(r)}
            >
              Restore
            </Button>
          ) : (
            <Button
              size="sm"
              variant="danger"
              loading={busyId === r.venueId}
              onClick={() => setRevoking(r)}
            >
              Revoke
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Licences"
        subtitle="Per-venue contractual term + cert health. Sorted by expiry — most urgent first."
      />

      <Card className="overflow-hidden">
        <DataState
          loading={licences.loading}
          error={licences.error}
          empty={!licences.loading && (licences.data ?? []).length === 0}
          emptyMessage="No venues yet."
        >
          <Table<LicenceRow>
            columns={columns}
            data={licences.data ?? []}
            keyExtractor={(r) => r.venueId}
          />
        </DataState>
      </Card>

      <ConfirmDialog
        isOpen={!!revoking}
        title="Revoke licence?"
        message={
          revoking
            ? `Revoke ${revoking.venueName}'s licence? The hub will lose its active cert and enter the 30-day soft-block grace window. Bookings, events and sync continue but the operator sees a banner asking them to contact support.`
            : ""
        }
        confirmLabel="Revoke"
        loading={!!revoking && busyId === revoking.venueId}
        onClose={() => setRevoking(null)}
        onConfirm={() => revoking && revoke(revoking)}
      />

      <RenewTermModal
        row={renewing}
        onClose={() => setRenewing(null)}
        onRenewed={() => {
          licences.refetch();
          setRenewing(null);
        }}
      />

      <TermHistoryModal
        row={historyFor}
        onClose={() => setHistoryFor(null)}
      />

      <Modal
        isOpen={!!revealKey}
        onClose={() => setRevealKey(null)}
        title="venueKey — copy now"
      >
        {revealKey && (
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              This is the only time you'll see <span className="font-medium">{revealKey.venueName}</span>'s
              new venueKey. Paste it into the hub's setup wizard before closing — the cloud
              only retains the bcrypt hash. After the hub claims, certs flow over WSS automatically.
            </div>
            <pre className="rounded bg-gray-50 border border-gray-200 p-3 text-xs overflow-x-auto select-all">
              {revealKey.venueKey}
            </pre>
            <div className="flex justify-end">
              <Button onClick={() => setRevealKey(null)}>Done</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function RenewTermModal({
  row,
  onClose,
  onRenewed,
}: {
  row: LicenceRow | null;
  onClose: () => void;
  onRenewed: () => void;
}) {
  const api = useApi();
  const { addToast } = useToast();
  const [startsAt, setStartsAt] = useState("");
  const [durationMonths, setDurationMonths] = useState(12);
  const [endsAt, setEndsAt] = useState("");
  const [endsTouched, setEndsTouched] = useState(false);
  const [planAtTerm, setPlanAtTerm] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-init defaults when a new row is opened.
  useEffect(() => {
    if (!row) return;
    const s = row.termEndsAt
      ? row.termEndsAt.slice(0, 10)
      : new Date().toISOString().slice(0, 10);
    setStartsAt(s);
    setEndsAt(addMonths(s, 12));
    setEndsTouched(false);
    setDurationMonths(12);
    setPlanAtTerm(row.planAtTerm ?? row.licencePlan);
    setNotes("");
    setError(null);
  }, [row?.venueId]);

  function close() {
    setStartsAt("");
    setEndsAt("");
    setEndsTouched(false);
    setDurationMonths(12);
    setPlanAtTerm("");
    setNotes("");
    setError(null);
    onClose();
  }

  async function submit() {
    if (!row) return;
    if (!startsAt || !endsAt) {
      setError("Both dates are required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await api(
        `/api/admin/venues/${row.venueId}/licence-terms/renew`,
        {
          method: "POST",
          body: JSON.stringify({
            startsAt: new Date(startsAt).toISOString(),
            endsAt: endsTouched ? new Date(endsAt).toISOString() : undefined,
            durationMonths,
            planAtTerm: planAtTerm || undefined,
            notes: notes.trim() || undefined,
          }),
        },
      );
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.error ?? "Failed to renew");
        return;
      }
      addToast(`${row.venueName}: term renewed`);
      onRenewed();
      close();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      isOpen={!!row}
      onClose={close}
      title={row ? `Renew term — ${row.venueName}` : "Renew term"}
    >
      {row && (
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            Current term ends{" "}
            <span className="font-medium">
              {row.termEndsAt ? new Date(row.termEndsAt).toLocaleDateString() : "—"}
            </span>
            . Renewal defaults to a 12-month period starting where the current term ends.
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Input
              label="Term starts"
              type="date"
              value={startsAt}
              onChange={(e) => {
                const next = e.target.value;
                setStartsAt(next);
                if (!endsTouched) setEndsAt(addMonths(next, durationMonths));
              }}
            />
            <Input
              label="Duration (months)"
              type="number"
              value={String(durationMonths)}
              onChange={(e) => {
                const n = Math.max(1, Math.min(120, Number(e.target.value) || 12));
                setDurationMonths(n);
                if (!endsTouched) setEndsAt(addMonths(startsAt, n));
              }}
            />
            <Input
              label="Term ends"
              type="date"
              value={endsAt}
              onChange={(e) => {
                setEndsTouched(true);
                setEndsAt(e.target.value);
              }}
            />
          </div>
          <Select
            label="Plan billed for this term"
            value={planAtTerm}
            onChange={(e) => setPlanAtTerm(e.target.value)}
            options={[
              { value: "standard", label: "Standard" },
              { value: "premium", label: "Premium" },
              { value: "enterprise", label: "Enterprise" },
            ]}
          />
          <Input
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="PO #1234, manual renewal etc."
          />
          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button onClick={submit} loading={submitting}>
              Renew term
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function TermHistoryModal({
  row,
  onClose,
}: {
  row: LicenceRow | null;
  onClose: () => void;
}) {
  const terms = useQuery<TermRow[]>(
    row ? `/api/admin/venues/${row.venueId}/licence-terms` : null,
  );

  return (
    <Modal isOpen={!!row} onClose={onClose} title={row ? `Term history — ${row.venueName}` : "Term history"}>
      {row && (
        <div className="space-y-3">
          <DataState
            loading={terms.loading}
            error={terms.error}
            empty={!terms.loading && (terms.data ?? []).length === 0}
            emptyMessage="No terms recorded yet."
          >
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {(terms.data ?? []).map((t) => (
                <div
                  key={t.id}
                  className={`rounded-md border p-3 text-sm ${
                    t.isCurrent ? "border-primary bg-primary/5" : "border-gray-200 bg-white"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-gray-900">
                      {new Date(t.startsAt).toLocaleDateString()} →{" "}
                      {new Date(t.endsAt).toLocaleDateString()}
                    </div>
                    <div className="flex gap-2">
                      <Badge className="bg-gray-100 text-gray-700">{t.origin}</Badge>
                      {t.isCurrent && (
                        <Badge className="bg-primary/10 text-primary">current</Badge>
                      )}
                      {t.coversNow && !t.isCurrent && (
                        <Badge className="bg-amber-100 text-amber-700">covers now</Badge>
                      )}
                    </div>
                  </div>
                  <div className="mt-1 text-xs text-gray-600">
                    {t.durationMonths} months · plan {t.planAtTerm}
                    {t.notes ? ` · ${t.notes}` : ""}
                  </div>
                </div>
              ))}
            </div>
          </DataState>
          <div className="flex justify-end pt-2">
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function relative(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return "";
  const days = Math.round(ms / (24 * 60 * 60 * 1000));
  if (days === 0) return "(today)";
  if (days > 0) return `(in ${days}d)`;
  return `(${-days}d ago)`;
}

function addMonths(iso: string, months: number): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
}
